import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * A recipe link a guest tried to import before being asked to sign up.
 *
 * Signing up navigates away from the sheet, so without this the link the user
 * had already pasted is gone by the time they come back — they'd have to go
 * find it again, which is exactly the moment people give up. Held across the
 * auth round trip and replayed once, then cleared.
 */

const KEY = 'paprika_pending_import_url';

/** Stale links are dropped: a URL from last week is not what the user meant. */
const MAX_AGE_MS = 30 * 60 * 1000;

interface StoredPendingImport {
  url: string;
  savedAt: number;
}

export async function setPendingImport(url: string): Promise<void> {
  try {
    const payload: StoredPendingImport = { url, savedAt: Date.now() };
    await AsyncStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // A lost hand-off is a small annoyance; a crash on the auth path is not.
    // Failing quietly keeps the sign-up flow working either way.
  }
}

/**
 * Read and clear in one step.
 *
 * Consuming on read is what stops the link replaying on every later visit to
 * the recipes screen — the sheet would reopen by itself long after the user
 * had moved on.
 */
export async function consumePendingImport(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(KEY);
    if (!raw) return null;
    await AsyncStorage.removeItem(KEY);

    const parsed = JSON.parse(raw) as StoredPendingImport;
    if (!parsed?.url || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;
    return parsed.url;
  } catch {
    return null;
  }
}

export async function clearPendingImport(): Promise<void> {
  try {
    await AsyncStorage.removeItem(KEY);
  } catch {
    // Nothing to recover; the age check above bounds a stale entry anyway.
  }
}
